import { ResponsiveTable, ResponsiveCell } from "../components/ResponsiveTable";
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import type { ContentType } from "@eaub/shared";
import { useApi } from "../api/ApiProvider";
import { useAsync } from "../lib/useAsync";
import { Spinner } from "../components/Spinner";
import { ErrorBanner } from "../components/ErrorBanner";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { formatDate } from "../lib/format";

export function ContentTypesList() {
  const api = useApi();
  const load = useCallback(() => api.listContentTypes(), [api]);
  const { data, loading, error, reload } = useAsync<ContentType[]>(load, [load]);

  const [toDelete, setToDelete] = useState<ContentType | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | undefined>(undefined);

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    setDeleteError(undefined);
    try {
      await api.deleteContentType(toDelete.id);
      setToDelete(null);
      reload();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Delete failed.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Content Types</h1>
          <p className="page__subtitle">Define the schemas that structure your content.</p>
        </div>
        <div className="page__actions">
          <Link to="/content-types/new" className="btn btn--primary">
            New content type
          </Link>
        </div>
      </div>

      {deleteError ? <ErrorBanner title="Could not delete" message={deleteError} /> : null}
      {loading ? <Spinner label="Loading…" /> : null}
      {error ? <ErrorBanner message={error.message} onRetry={reload} /> : null}

      {data ? (
        data.length === 0 ? (
          <div className="empty-state">
            <p>No content types defined.</p>
            <Link to="/content-types/new" className="btn btn--primary">
              Create one
            </Link>
          </div>
        ) : (
          <ResponsiveTable label="Content types">
            <thead role="rowgroup">
              <tr role="row">
                <th scope="col" role="columnheader">Name</th>
                <th scope="col" role="columnheader">Slug</th>
                <th scope="col" role="columnheader">Fields</th>
                <th scope="col" role="columnheader">Updated</th>
                <th scope="col" role="columnheader" className="table__actions-col">Actions</th>
              </tr>
            </thead>
            <tbody role="rowgroup">
              {data.map((type) => (
                <tr role="row" key={type.id}>
                  <ResponsiveCell label="Name">
                    <Link to={`/content-types/${type.id}/entries`} className="table__link">
                      {type.name}
                    </Link>
                  </ResponsiveCell>
                  <ResponsiveCell label="Slug">
                    <code>{type.slug}</code>
                  </ResponsiveCell>
                  <ResponsiveCell label="Fields">{type.fields.length}</ResponsiveCell>
                  <ResponsiveCell label="Updated" className="muted">{formatDate(type.updatedAt)}</ResponsiveCell>
                  <ResponsiveCell label="Actions" className="table__actions">
                    <Link to={`/content-types/${type.id}/entries`} className="btn btn--ghost btn--sm">
                      Entries
                    </Link>
                    <Link to={`/content-types/${type.id}/edit`} className="btn btn--secondary btn--sm">
                      Edit
                    </Link>
                    <button
                      type="button"
                      className="btn btn--danger btn--sm"
                      onClick={() => setToDelete(type)}
                    >
                      Delete
                    </button>
                  </ResponsiveCell>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
        )
      ) : null}

      {toDelete ? (
        <ConfirmDialog
          title="Delete content type"
          message={`Delete “${toDelete.name}”? This cannot be undone. If entries of this type exist, the server will reject the request.`}
          confirmLabel="Delete"
          danger
          busy={deleting}
          onConfirm={() => void confirmDelete()}
          onCancel={() => {
            setToDelete(null);
            setDeleteError(undefined);
          }}
        />
      ) : null}
    </div>
  );
}
